import { useState } from 'react';
import { cn } from '@/lib/utils';
import { chatAvatarUrl } from '../utils';

interface UserAvatarProps {
  userId: string;
  displayName: string;
  /** Single-character fallback shown until/unless the image loads. */
  initial: string;
  /** Size + extra container classes (default `h-9 w-9`). */
  className?: string;
  /** Background + text classes for the fallback initial layer. */
  fallbackClassName?: string;
  /** CSS background (color or gradient) for the fallback layer. */
  background?: string;
}

/**
 * Renders a chat user's avatar: the initial as the base layer with the image
 * overlaid, revealed on load and hidden on error. The image URL is keyed
 * purely on userId and served + presigned by the API, so any user's avatar
 * resolves uniformly — current user, other members, senders beyond the
 * member-list page, and WebSocket-delivered messages alike.
 *
 * This replaces the old per-member `getDownloadUrl(avatarImageId)` approach,
 * which could only ever sign the *current* user's own avatar: avatars are
 * private per-user Vault docs and the Vault download route enforces holder
 * access, so a client signing another member's avatar always 403'd. A user
 * with no avatar 404s here and simply shows the initial.
 */
export function UserAvatar({
  userId,
  displayName,
  initial,
  className = 'h-9 w-9',
  fallbackClassName = 'bg-primary/10 text-xs font-medium text-primary',
  background,
}: UserAvatarProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  return (
    <div className={cn('relative rounded-full overflow-hidden flex-shrink-0', className)}>
      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center select-none',
          fallbackClassName,
        )}
        style={background ? { background } : undefined}
      >
        {initial}
      </div>
      {!errored && (
        <img
          src={chatAvatarUrl(userId)}
          alt={displayName}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
        />
      )}
    </div>
  );
}
